/**
 * How an exercise's load is prescribed: absolute kilograms, or a percent of the client's one-rep max.
 *
 * There used to be a third value, `ClientSelected` — "the working sets deliberately carry no load,
 * the client picks it". The measurement list answers that structurally now (execution ADR, D3/D5):
 * an exercise that does not measure weight has no weight column anywhere, and one that measures
 * weight with an empty cell has a column for the client to fill. The flag was a second way to say
 * the same thing, and the two could not both be true at once.
 *
 * **Do not try to derive the mode from the values either.** `prescribedPercent` is genuinely
 * overloaded — a percent of 1RM on an ordinary set, a percent of the top set's ACTUAL on a ladder
 * member — so inferring the mode from a present percent reads a back-off as a percent prescription.
 * The mode is DATA off the wire and these helpers keep every consumer reading it rather than
 * re-deducing it.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { SetDraft } from './planDraft.types';
import { BACKOFF_PERCENT_DEFAULT, derivesLoadInSession, isBackoffSet } from './setShape';

/** The two explicit prescription modes, as the wire spells them. */
export const PRESCRIPTION_MODE = {
  absolute: 'AbsoluteWeight',
  percent: 'PercentOfOneRm',
} as const;

/** True when the exercise is in %-of-1RM mode. */
export function isPercentMode(prescriptionMode: string): boolean {
  return prescriptionMode === PRESCRIPTION_MODE.percent;
}

/** The other mode — what the toggle switches to. */
export function togglePrescriptionMode(prescriptionMode: string): string {
  return isPercentMode(prescriptionMode) ? PRESCRIPTION_MODE.absolute : PRESCRIPTION_MODE.percent;
}

/**
 * Whether switching mode would DISCARD an authored load — the gate on the switch's confirmation; a
 * mode with nothing to lose flips silently.
 *
 * Warm-ups count: the switch clears every row, so a load sitting only on warm-up rows would still
 * be lost. **A ladder member's percent does not**, because the switch deliberately leaves it
 * standing — it is the descent's construction rather than a load prescription, and warning about
 * work that is not going anywhere is how a confirmation stops being believed.
 */
export function hasAuthoredLoad(sets: readonly SetDraft[]): boolean {
  return sets.some(
    (set) =>
      set.prescribedWeight != null ||
      (set.prescribedPercent != null && !derivesLoadInSession(set)),
  );
}

/**
 * Put one set's load on the side of the fence its ROLE and its exercise's MODE require.
 *
 * A **back-off is the structural exception**: its `prescribedPercent` is the percent-of-TOP, not a
 * weight prescription. It is meaningful in both modes — a descent hangs off the top's load whether
 * the top is prescribed in kg or in %1RM — and the backend REQUIRES every ladder member to carry
 * one while REFUSING it an authored weight. So in both modes the weight always goes, the percent
 * always stays, and a descent that somehow arrives without one is seeded the default rather than
 * shipping a percent-less ladder member the save would refuse.
 *
 * Every other type follows its exercise's mode. That fall-through is also what makes a back-off →
 * AMRAP change legal: the descent's percent-of-top is not an AMRAP prescription, so kg mode clears
 * it and leaves the percent-LESS AMRAP the table actually renders, rather than an invisible
 * in-block descent.
 */
export function normalizeSetLoad(set: SetDraft, mode: string): SetDraft {
  if (isBackoffSet(set)) {
    return {
      ...set,
      prescribedWeight: null,
      prescribedPercent: set.prescribedPercent ?? BACKOFF_PERCENT_DEFAULT,
    };
  }
  if (isPercentMode(mode)) return { ...set, prescribedWeight: null };
  return { ...set, prescribedPercent: null };
}
